<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '@/logger.ts'
import { onMounted, ref } from 'vue'
import { SvgIcon } from "@progress/kendo-vue-common";
import type { UploadFileInfo } from '@progress/kendo-vue-upload'
import { TextArea  as KTextArea, Checkbox as KCheckBox  } from '@progress/kendo-vue-inputs'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import Swal from 'sweetalert2'
import {
  GetFullProductImageDictionary,
  GetCachedProductDetails
} from '@/services/SkuService.ts'
import type { IProductExWithSkuExsViewModel } from '@/types/SkuTypes'
import {
  GetOrderTypeId,
  GetBeeNumberAuthenicatedUser,
  GetReviewRootUrl,
  GetProductRootUrl
} from '@/stores/SettingsStore.ts'
import { starIcon, starOutlineIcon, warningCircleIcon } from '@progress/kendo-svg-icons'
import { ProductReviewAdd, ProductReviewEdit, ProductReviewFileAdd, ClearProductReviewCache } from '@/services/ProductService'
import { ProductReviewFileDelete } from '@/api/ProductReview'
import type { IProductReviewFileResponse, IAoeProductReviewAttachment } from '@/types/ProductReviewTypes'
import { SetIsLoading } from '@/services/AoeHelpers.ts'
import { UrlHelpers } from '@/utils/urlHelpers'

const logger = useLogger()
setStoreLogger(logger)


const orderTypeId = ref<string>('')
const productCode = ref<string | null>('')
const productUrl = ref<string | null>('')
const productImageUrls = ref<Record<string, string>>({})
const productsDetails = ref<Array<IProductExWithSkuExsViewModel>>(
  [],
)

const txtComments = ref<string>('')
const isAnonymous = ref<boolean>(false)
const files = ref<UploadFileInfo[]>([])

const isEditMode = ref<boolean>(false)
const reviewId = ref<number | null>(null)
const existingAttachments = ref<IAoeProductReviewAttachment[]>([])
const reviewRootUrl = ref<string>('')
const productRootUrl = ref<string>('')

const errorRating = ref<boolean>(false)
const errorComments = ref<boolean>(false)
const errorUser = ref<boolean>(false)

const rating = ref<number>(0)
const starOne = ref<boolean>(false)
const starTwo = ref<boolean>(false)
const starThree = ref<boolean>(false)
const starFour = ref<boolean>(false)
const starFive = ref<boolean>(false)

onMounted(async () => {
  orderTypeId.value = await GetOrderTypeId()
  reviewRootUrl.value = await GetReviewRootUrl()
  productRootUrl.value = await GetProductRootUrl()

  const urlParams = new URLSearchParams(window.location.search)
  productCode.value = urlParams.get('productCode')
  productUrl.value = urlParams.get('returnURL')
  const mode = urlParams.get('mode')
  const reviewIdParam = urlParams.get('reviewId')

  // Check if we're in edit mode
  if (mode === 'edit' && reviewIdParam) {
    isEditMode.value = true
    reviewId.value = parseInt(reviewIdParam)

    // Load review data from localStorage
    const storedReviewData = localStorage.getItem('editReviewData')
    if (storedReviewData) {
      try {
        const reviewData = JSON.parse(storedReviewData)

        // Populate form fields
        txtComments.value = reviewData.comments || ''
        rating.value = reviewData.rating || 0
        isAnonymous.value = reviewData.isAnonymous || false

        // Load existing attachments
        if (reviewData.attachments && Array.isArray(reviewData.attachments)) {
          existingAttachments.value = reviewData.attachments
        }

        // Set star ratings based on rating value
        setRating(reviewData.rating || 0)

        // Clean up localStorage
        localStorage.removeItem('editReviewData')
      } catch (error) {
        logger.error('Error parsing review data from localStorage:', error)
      }
    }
  }

  if (productCode.value != null)
  {
    const productCodes: string[] = [ productCode.value ]
    productImageUrls.value = await GetFullProductImageDictionary(productCodes)

    productsDetails.value = await GetCachedProductDetails(
      orderTypeId.value,
      productCodes,
    )
  }
})

async function submitReviewClickedHandler() {
  let error = false

  if (rating.value == 0)
  {
    errorRating.value = true
    error = true
  }

  if (txtComments.value == '')
  {
    errorComments.value = true
    error = true
  }

  const beeNumberAuthenicatedUser = await GetBeeNumberAuthenicatedUser()
  if (beeNumberAuthenicatedUser == null)
  {
    errorUser.value = true
    error = true
  }

  if (error) {
    return
  }

  SetIsLoading(true)

  let productReviewId: number

  try {
    if (isEditMode.value && reviewId.value) {
      // Update existing review
      productReviewId = await ProductReviewEdit({
        aoeProductReviewId: reviewId.value,
        productCode: productCode.value!,
        title: '',
        comments: txtComments.value,
        beeNumber: beeNumberAuthenicatedUser!,
        rating: rating.value,
        isApproved: true,
        isAnonymous: isAnonymous.value
      })
    } else {
      // Create new review
      productReviewId = await ProductReviewAdd({
        productCode: productCode.value!,
        title: '',
        comments: txtComments.value,
        beeNumber: beeNumberAuthenicatedUser!,
        rating: rating.value,
        isAnonymous: isAnonymous.value
      })
    }
  } catch (error) {
    logger.error('Error submitting review:', error)
    SetIsLoading(false)
    await Swal.fire({
      title: t('productReview.ErrorHeader'),
      text: t('productReview.ErrorSubHeader'),
      icon: 'error',
      confirmButtonText: t('labels.Okay')
    })
    return
  }

  if (productReviewId) {
    // Clear the review cache so the product list page shows updated ratings
    if (productCode.value) {
      ClearProductReviewCache(productCode.value)
    }

    let hasFileError = false

    for (const file of files.value) {
      if (file != undefined && file.getRawFile != undefined && !hasFileError)
      {
        // Actualizar estado del archivo a "uploading"
        file.status = 1 // 1 = Uploading
        file.progress = 0

        const blob = file.getRawFile()

        const formData = new FormData();
        formData.append('file', blob);

        await ProductReviewFileAdd({
                    productCode: productCode.value!,
                    aoeProductReviewId: productReviewId,
                    fileType: file.extension != undefined ? file.extension.replace('.', '') : ''
                  },
          formData).then(response => {
            const rp = response as IProductReviewFileResponse
            if (rp.error == null)
            {
              // Marcar archivo como completado
              file.status = 2 // 2 = Uploaded
              file.progress = 100
            }
            else
            {
              // Marcar archivo como fallido
              file.status = 3 // 3 = Failed
              hasFileError = true
            }
        })

      }
    }

    // Turn off loading before showing alert
    SetIsLoading(false)

    // Show appropriate feedback and redirect
    if (hasFileError) {
      await Swal.fire({
        title: t('productReview.ErrorHeader'),
        html: `${t('productReview.ErrorSubHeader')}<br><br>${t('productReview.ErrorFileSize')}`,
        icon: 'error',
        confirmButtonText: t('labels.Okay')
      })
    } else {
      await Swal.fire({
        title: t('productReview.CompleteHeader'),
        text: t('productReview.CompleteSubHeader'),
        icon: 'success',
        confirmButtonText: t('labels.Okay')
      })

      // Redirect to return URL after user clicks OK
      const safeProductUrl = UrlHelpers.getSafeReturnUrl(productUrl.value, [productRootUrl.value, reviewRootUrl.value])
      if (safeProductUrl) {
        window.location.href = safeProductUrl
      }
    }
  } else {
    logger.error('productReviewId is null or undefined')
    SetIsLoading(false)
  }
}

function GetFullProductImageUrlFromStringDictionary(productCode: string | null) {
  if (productCode != null)
  {
    const productImageUrl = productImageUrls.value[productCode]
    if (productImageUrl) return productImageUrl
  }
  return '#'
}

function cancelReviewClickedHandler() {
  const safeProductUrl = UrlHelpers.getSafeReturnUrl(productUrl.value, [productRootUrl.value, reviewRootUrl.value])
  if (safeProductUrl) {
    window.location.href = safeProductUrl
  }
}

function starClickedHandler(starNumber: number) {
  rating.value = starNumber
  errorRating.value = false
  starLeaveHandler()
}

function setRating(ratingValue: number) {
  rating.value = ratingValue
  // Stars are displayed based on rating.value, no need to set individual star refs
}

function starHoverHandler(starNumber: number) {
  if (starNumber == 1) {
    starOne.value = true
  } else if (starNumber == 2) {
    starOne.value = true
    starTwo.value = true
  } else if (starNumber == 3) {
    starOne.value = true
    starTwo.value = true
    starThree.value = true
  } else if (starNumber == 4) {
    starOne.value = true
    starTwo.value = true
    starThree.value = true
    starFour.value = true
  } else if (starNumber == 5) {
    starOne.value = true
    starTwo.value = true
    starThree.value = true
    starFour.value = true
    starFive.value = true
  }
}

function starLeaveHandler() {
  starOne.value = false
  starTwo.value = false
  starThree.value = false
  starFour.value = false
  starFive.value = false
}


const fileInput = ref<HTMLInputElement>()

// Maneja la selección de archivos desde el input nativo
function onFileSelected(event: Event) {
  const target = event.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    const newFiles: UploadFileInfo[] = Array.from(target.files).map(file => ({
      uid: `${Date.now()}-${Math.random()}`,
      name: file.name,
      size: file.size,
      extension: '.' + file.name.split('.').pop(),
      status: 0,
      progress: 0,
      getRawFile: () => file
    }))
    files.value = [...files.value, ...newFiles]
  }
}

function removeFile(index: number) {
  files.value.splice(index, 1)
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

function formatFileSize(bytes: number | undefined): string {
  if (!bytes || bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

async function deleteExistingAttachment(attachmentId: number) {
  const result = await Swal.fire({
    title: t('alerts.DeleteConfirmTitle'),
    text: t('productReview.DeleteAttachmentConfirm'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('buttons.Yes'),
    cancelButtonText: t('buttons.No'),
  })
  if (!result.isConfirmed) return

  try {
    // Call API to delete attachment
    await ProductReviewFileDelete({ aoeProductReviewId: reviewId.value!, aoeProductReviewAttachmentId: attachmentId })

    // Remove from display
    existingAttachments.value = existingAttachments.value.filter(
      att => att.aoeProductReviewAttachmentId !== attachmentId
    )

    logger.info('Attachment deleted successfully')
  } catch (error) {
    logger.error('Error deleting attachment:', error)
    await Swal.fire({
      title: t('productReview.ErrorHeader'),
      text: t('productReview.ErrorDeletingAttachment'),
      icon: 'error',
      confirmButtonText: t('labels.Okay')
    })
  }
}

</script>

<template>
  <div class="productReviewSection">
    <div class="row">
      <div class="col-12">
        <p class="pageHeader">{{ t('productReview.CreateReview') }}</p>
      </div>
    </div>
    <div v-if="errorUser" class="row mt-3">
      <div class="col-12 submitError">
        <SvgIcon :icon="warningCircleIcon" :size="'medium'"></SvgIcon>&nbsp;&nbsp;&nbsp;{{ t('productReview.ErrorLoggedIn') }}
      </div>
    </div>
    <div class="row mt-2">
      <div class="col-2">
        <img
          :src="
            GetFullProductImageUrlFromStringDictionary(
              productCode
            )
          "
          class="img-fluid prodImage"
          :alt="t('cart.ProductImageAlt')"
        />
      </div>
      <div class="col-10">
        <p class="pageSubHeader">{{ productsDetails[0]?.productName }}</p>
      </div>
    </div>
    <hr>
    <div class="row mt-2">
      <div class="col-12">
        <p class="pageSubHeader">{{ t('productReview.OverallRating') }}</p>
      </div>
    </div>
    <div class="row ratingsRow">
      <div class="col-2">
        <KButton
          v-if="!starOne  && rating < 1"
          style="transform: scale(2.5)"
          class="starColor"
          :svg-icon="starOutlineIcon"
          :fill-mode="'clear'"
          @mouseover="starHoverHandler(1)"
        ></KButton>
        <KButton
          v-if="starOne || rating >= 1"
          style="transform: scale(2.5)"
          class="starColor"
          :svg-icon="starIcon"
          :fill-mode="'clear'"
          @click="starClickedHandler(1)"
          @mouseleave="starLeaveHandler"
        ></KButton>
      </div>
      <div class="col-2">
        <KButton
          v-if="!starTwo && rating < 2"
          style="transform: scale(2.5)"
          class="starColor"
          :svg-icon="starOutlineIcon"
          :fill-mode="'clear'"
          @mouseover="starHoverHandler(2)"
        ></KButton>
        <KButton
          v-if="starTwo  || rating > 1"
          style="transform: scale(2.5)"
          class="starColor"
          :svg-icon="starIcon"
          :fill-mode="'clear'"
          @click="starClickedHandler(2)"
          @mouseleave="starLeaveHandler"
        ></KButton>
      </div>
      <div class="col-2">
        <KButton
          v-if="!starThree  && rating < 3"
          style="transform: scale(2.5)"
          class="starColor"
          :svg-icon="starOutlineIcon"
          :fill-mode="'clear'"
          @mouseover="starHoverHandler(3)"
        ></KButton>
        <KButton
          v-if="starThree || rating > 2"
          style="transform: scale(2.5)"
          class="starColor"
          :svg-icon="starIcon"
          :fill-mode="'clear'"
          @click="starClickedHandler(3)"
          @mouseleave="starLeaveHandler"
        ></KButton>
      </div>
      <div class="col-2">
        <KButton
          v-if="!starFour  && rating < 4"
          style="transform: scale(2.5)"
          class="starColor"
          :svg-icon="starOutlineIcon"
          :fill-mode="'clear'"
          @mouseover="starHoverHandler(4)"
        ></KButton>
        <KButton
          v-if="starFour || rating > 3"
          style="transform: scale(2.5)"
          class="starColor"
          :svg-icon="starIcon"
          :fill-mode="'clear'"
          @click="starClickedHandler(4)"
          @mouseleave="starLeaveHandler"
        ></KButton>
      </div>
      <div class="col-2">
        <KButton
          v-if="!starFive && rating < 5"
          style="transform: scale(2.5)"
          class="starColor"
          :svg-icon="starOutlineIcon"
          :fill-mode="'clear'"
          @mouseover="starHoverHandler(5)"
        ></KButton>
        <KButton
          v-if="starFive || rating == 5"
          style="transform: scale(2.5)"
          class="starColor"
          :svg-icon="starIcon"
          :fill-mode="'clear'"
          @click="starClickedHandler(5)"
          @mouseleave="starLeaveHandler"
        ></KButton>
      </div>
    </div>
    <div v-if="errorRating" class="row mt-3">
      <div class="col-12 submitError">
        <SvgIcon :icon="warningCircleIcon" :size="'medium'"></SvgIcon>&nbsp;&nbsp;&nbsp;{{ t('productReview.ErrorOverallRating') }}
      </div>
    </div>
    <hr>
    <div class="row mt-2">
      <div class="col-12">
        <p class="pageSubHeader">{{ t('productReview.ReviewAttachment') }}</p>
      </div>
    </div>

    <!-- Existing Attachments (Edit Mode) -->
    <div v-if="isEditMode && existingAttachments.length > 0" class="row mb-3">
      <div class="col-12">
        <div class="existing-attachments-wrapper">
          <p class="existing-attachments-label">{{ t('productReview.ExistingAttachments') }}</p>
          <div class="existing-attachments-grid">
            <div v-for="attachment in existingAttachments" :key="attachment.aoeProductReviewAttachmentId" class="existing-attachment-item">
              <div class="attachment-preview">
                <img
                  v-if="!attachment.filePath.includes('.mp4')"
                  :src="reviewRootUrl + '/' + attachment.filePath"
                  :alt="t('productReview.ReviewImageAlt')"
                  class="attachment-thumbnail"
                />
                <video
                  v-if="attachment.filePath.includes('.mp4')"
                  :src="reviewRootUrl + '/' + attachment.filePath"
                  class="attachment-thumbnail"
                  controls
                ></video>
              </div>
              <button
                type="button"
                class="delete-attachment-btn"
                @click="deleteExistingAttachment(attachment.aoeProductReviewAttachmentId)"
                :title="t('productReview.DeleteAttachment')"
              >
                {{ t('productReview.Delete') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="compact-upload-wrapper">
          <div class="compact-upload-dropzone">
            <input
              ref="fileInput"
              type="file"
              @change="onFileSelected"
              accept=".jpeg,.png,.jpg,.gif,.mp4,image/jpeg,image/png,image/gif,video/mp4"
              multiple
              class="file-input-hidden"
              id="review-file-upload"
            />
            <label for="review-file-upload" class="compact-upload-label">
              <span class="upload-text">{{ t('productReview.ClickToUpload') }}</span>
              <span class="upload-hint">{{ t('productReview.SupportedFormats') }}</span>
            </label>
          </div>

          <!-- Selected Files List -->
          <div v-if="files.length > 0" class="compact-files-list">
            <div v-for="(file, index) in files" :key="file.uid" class="compact-file-item">
              <div class="file-info-row">
                <span class="file-icon">📄</span>
                <span class="file-name-compact">{{ file.name }}</span>
                <span class="file-size-compact">{{ formatFileSize(file.size) }}</span>
                <button type="button" class="remove-btn-compact" @click="removeFile(index)" :title="t('productReview.RemoveFileTitle')">×</button>
              </div>
              <!-- Progress bar (se muestra durante upload) -->
              <div v-if="file.status === 1" class="progress-bar-compact">
                <div class="progress-fill" :style="{ width: file.progress + '%' }"></div>
              </div>
              <!-- Status indicators -->
              <div v-if="file.status === 2" class="status-success">✓ {{ t('productReview.Uploaded') }}</div>
              <div v-if="file.status === 3" class="status-error">✗ {{ t('productReview.Failed') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <hr>
    <div class="row mt-2">
      <div class="col-12">
        <p class="pageSubHeader">{{ t('productReview.ReviewComment') }}</p>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <KTextArea
          :placeholder="t('productReview.CommentPlaceholder')"
          v-model="txtComments"
          :rounded="'large'"
          :theme-color="'primary'"
          :rows="10"
          @change="() => { errorComments = false }"
        ></KTextArea>
      </div>
    </div>
    <div v-if="errorComments" class="row mt-3">
      <div class="col-12 submitError">
        <SvgIcon :icon="warningCircleIcon" :size="'medium'"></SvgIcon>&nbsp;&nbsp;&nbsp;{{ t('productReview.ErrorReviewComment') }}
      </div>
    </div>
    <div class="row mt-2">
      <div class="col-12">
        <KCheckBox
          class="chbxAnonymous"
          v-model="isAnonymous">
          {{ t('productReview.ReviewAnonymous') }}
        </KCheckBox>
      </div>
    </div>
    <div class="row mt-2">
      <div class="col-12 reviewSubmitBtnDiv">
        <div class="btnReviewAction d-inline-block me-2">
          <button class="text-center btn" @click="cancelReviewClickedHandler">
              {{ t('buttons.Cancel') }}
          </button>
        </div>
        <div class="btnReviewAction d-inline-block">
          <button class="text-center btn primaryBtn" @click="submitReviewClickedHandler">
              {{ t('productReview.ReviewSubmit') }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<style lang="scss">

.chbxAnonymous .k-checkbox
{
  border: solid 1px black !important;
  margin-right: 10px;
}

.reviewSubmitBtnDiv {
  text-align: right;
}

.btnReviewAction {
  button.btn {
    font-weight: $font-weight-bold;
    border-radius: $button-radius;
    text-transform: uppercase !important;
    background-color: #477981;
    color: white;
    border: none;
    padding: 10px 20px;
    cursor: pointer;

    &:hover {
      background-color: #3a6269;
    }

    &:disabled {
      background-color: #cccccc;
      cursor: not-allowed;
    }
  }
}

.starColor {
  color: #ffaa45;
}

.ratingsRow
{
  width: 400px;
}

.submitError
{
  color:red;
}

/* Compact Upload Styles */
.compact-upload-wrapper {
  border: 1px solid #e0e0e0;
  border-radius: $radius-md;
  overflow: hidden;
}

.compact-upload-dropzone {
  background: #fefefe;
  border: 2px dashed #e0e0e0;
  border-radius: $radius-sm;
  padding: 1rem;
  text-align: center;
  transition: all 0.2s ease;
  cursor: pointer;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.compact-upload-dropzone:hover {
  border-color: #ffaa45;
  background: #fff8f0;
}

.file-input-hidden {
  display: none;
}

.compact-upload-label {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
}

.upload-icon {
  @include typography-h4;
}

.upload-text {
  @include typography-body;
  @include font-weight-medium;
  color: #333;
}

.upload-hint {
  @include typography-caption;
  color: #666;
  margin-left: 0.5rem;
}

.compact-files-list {
  max-height: 200px;
  overflow-y: auto;
  padding: 0.5rem;
}

.compact-file-item {
  background: var(--vt-c-white-soft);
  border: 1px solid #e9ecef;
  border-radius: $radius-sm;
  padding: 0.5rem;
  margin-bottom: 0.5rem;
}

.file-info-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.file-icon {
  @include typography-body;
  flex-shrink: 0;
}

.file-name-compact {
  @include typography-small;
  flex: 1;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-size-compact {
  @include typography-caption;
  color: #666;
  padding: 0.15rem 0.4rem;
  background: #e9ecef;
  border-radius: $radius-sm;
  font-family: monospace;
  flex-shrink: 0;
}

.remove-btn-compact {
  background: #dc3545;
  color: white;
  border: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  @include typography-body;
  cursor: pointer;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  flex-shrink: 0;
}

.remove-btn-compact:hover {
  background: #c82333;
}

.progress-bar-compact {
  margin-top: 0.4rem;
  height: 4px;
  background: #e9ecef;
  border-radius: $radius-xs;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #ffaa45;
  transition: width 0.3s ease;
}

.status-success {
  @include typography-caption;
  @include font-weight-medium;
  margin-top: 0.3rem;
  color: #28a745;
}

.status-error {
  @include typography-caption;
  @include font-weight-medium;
  margin-top: 0.3rem;
  color: #dc3545;
}

/* Existing Attachments Styles */
.existing-attachments-wrapper {
  border: 1px solid #e0e0e0;
  border-radius: $radius-md;
  padding: $spacing-4;
  background: var(--vt-c-white-soft);
  margin-bottom: $spacing-4;
}

.existing-attachments-label {
  @include typography-body;
  @include font-weight-semibold;
  color: #333;
  margin-bottom: $spacing-3;
}

.existing-attachments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: $spacing-3;
}

.existing-attachment-item {
  display: flex;
  flex-direction: column;
  gap: $spacing-2;
  border: 1px solid #e9ecef;
  border-radius: $radius-sm;
  padding: $spacing-2;
  background: white;
}

.attachment-preview {
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: $radius-sm;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.attachment-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.delete-attachment-btn {
  @include typography-small;
  @include font-weight-medium;
  background: white;
  color: #dc3545;
  border: 1px solid #dc3545;
  padding: $spacing-1 $spacing-2;
  border-radius: $radius-sm;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  text-align: center;
}

.delete-attachment-btn:hover {
  background: #dc3545;
  color: white;
}

@media (max-width: 768px) {
  .existing-attachments-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
}
</style>
